import {assert} from 'Asserts';
import {Container, GridCellOrEmptyGridCell, isInaccessibleStorable} from 'constants/SampleConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import {MeasurementWithNewUnit, convertToStandard} from 'inventory/InventoryUnitUtil';
import {CoordinatesOrderingKey, GridCellTableTableRow, SortKey} from 'samples/grids/GridCellTableConstants';
import {
  getTagIdFromSortKey,
  getTagNameFromSortKey,
  isContentsTagSortKey,
  isTagSortKey,
} from 'schemas/tags/ExpandedTableTagUtil';

export function compareCoordinatesRowMajor(a: GridCellOrEmptyGridCell, b: GridCellOrEmptyGridCell): number {
  return a.rowIndex - b.rowIndex || a.columnIndex - b.columnIndex;
}

export function compareCoordinatesColumnMajor(
  a: GridCellOrEmptyGridCell,
  b: GridCellOrEmptyGridCell
): number {
  return a.columnIndex - b.columnIndex || a.rowIndex - b.rowIndex;
}

export function compareQuantity(a?: MeasurementWithNewUnit, b?: MeasurementWithNewUnit): number {
  const standardizedVolumeA = a && convertToStandard(a);
  const standardizedVolumeB = b && convertToStandard(b);
  // It also returns 0 when the actual values are equal, but that is handled by the minus operation in the
  // else case since === doesn't work on decimals & the decimal equals operation doesn't work on null values.
  if (standardizedVolumeA == null && standardizedVolumeB == null) {
    return 0;
  } else if (standardizedVolumeA == null) {
    return -1;
  } else if (standardizedVolumeB == null) {
    return 1;
  } else {
    return standardizedVolumeA.minus(standardizedVolumeB).toNumber();
  }
}

function getNormalizedTagValue(tags: Array<SchemaTag>, tagName: string, tagId?: number): string {
  // Different fields with the same name from different container schemas are rendered in the same column
  // in the table so a tag not existing is expected behavior. When this happens, treat it like an empty value
  // for sorting purposes.
  // If tagId is provided, should match tag by id instead of name
  const matchedTag = tags.find((tag) =>
    tagId !== undefined ? tag.tagSchemaField.id === tagId : tag.name === tagName
  );
  if (matchedTag?.value == null) {
    return '';
  } else {
    return matchedTag.value.toUpperCase();
  }
}

function compareNormalizedTags(
  normalizedTagValueA: string,
  normalizedTagValueB: string,
  separatorToParseNumbers?: string
): number {
  if (normalizedTagValueA === normalizedTagValueB) {
    return 0;
  } else if (
    normalizedTagValueA.trim().length > 0 &&
    normalizedTagValueB.trim().length > 0 &&
    !Number.isNaN(Number(normalizedTagValueA)) &&
    !Number.isNaN(Number(normalizedTagValueB))
  ) {
    return parseFloat(normalizedTagValueA) < parseFloat(normalizedTagValueB) ? -1 : 1;
  } else if (
    separatorToParseNumbers !== undefined &&
    normalizedTagValueA.split(separatorToParseNumbers).every((n) => !Number.isNaN(Number(n))) &&
    normalizedTagValueB.split(separatorToParseNumbers).every((n) => !Number.isNaN(Number(n)))
  ) {
    const aNumbers = normalizedTagValueA.split(separatorToParseNumbers).map((n) => Number(n));
    const bNumbers = normalizedTagValueB.split(separatorToParseNumbers).map((n) => Number(n));
    // Compare lists of numbers item by item
    for (let idx = 0; idx < aNumbers.length; idx++) {
      if (idx < bNumbers.length) {
        if (aNumbers[idx] < bNumbers[idx]) {
          return -1;
        } else if (aNumbers[idx] > bNumbers[idx]) {
          return 1;
        }
      } else {
        return 1; // aNumbers is longer
      }
    }
    return -1; // bNumbers is longer
  } else {
    return normalizedTagValueA < normalizedTagValueB ? -1 : 1;
  }
}

export function compareTag(a: Container, b: Container, tagName: string): number {
  const normalizedTagValueA = getNormalizedTagValue(a.tags, tagName);
  const normalizedTagValueB = getNormalizedTagValue(b.tags, tagName);
  return compareNormalizedTags(normalizedTagValueA, normalizedTagValueB);
}

export function compareContentsTag(aTags: Array<SchemaTag>, bTags: Array<SchemaTag>, tagId: number): number {
  const normalizedTagValueA = getNormalizedTagValue(aTags, '', tagId);
  const normalizedTagValueB = getNormalizedTagValue(bTags, '', tagId);
  return compareNormalizedTags(normalizedTagValueA, normalizedTagValueB, '\n');
}

export function compareFunction(
  rowA: GridCellTableTableRow,
  rowB: GridCellTableTableRow,
  sortKey: string,
  customOrderingBehaviorKey?: string
): number {
  const a = rowA.extra.cell;
  const b = rowB.extra.cell;
  if (sortKey === SortKey.COORDINATES && customOrderingBehaviorKey === CoordinatesOrderingKey.COLUMN) {
    return compareCoordinatesColumnMajor(a, b);
  } else if (sortKey === SortKey.COORDINATES) {
    return compareCoordinatesRowMajor(a, b);
  }
  // Rows with empty positions (null containers) or inaccessible containers should be sorted to the bottom
  // for any sort key other than coordinates
  else if (
    (a.container == null || isInaccessibleStorable(a.container)) &&
    (b.container == null || isInaccessibleStorable(b.container))
  ) {
    return 0;
  } else if (a.container == null || isInaccessibleStorable(a.container)) {
    return -1;
  } else if (b.container == null || isInaccessibleStorable(b.container)) {
    return 1;
  } else if (sortKey === SortKey.VOLUME) {
    return compareQuantity(rowA.extra.containerQuantity, rowB.extra.containerQuantity);
  } else if (isTagSortKey(sortKey)) {
    const tagName = getTagNameFromSortKey(sortKey);
    return compareTag(a.container, b.container, tagName);
  } else if (isContentsTagSortKey(sortKey)) {
    const tagId = getTagIdFromSortKey(sortKey);
    const aTags = rowA.extra.containedTags!;
    const bTags = rowB.extra.containedTags!;
    return compareContentsTag(aTags, bTags, tagId);
  } else {
    assert(false, `Unsupported sortKey ${sortKey}`);
    return 0;
  }
}
